import axios from 'axios'
import Cookies from 'js-cookie'

export default {
  namespaced: true,
  
  state: {
    tournaments: [],
    activeTournament: null,
    tournamentBracket: null,
    upcomingMatches: [],
    recentMatches: [],
    loading: {
      tournaments: false,
      details: false,
      bracket: false,
      schedule: false,
      registration: false
    },
    error: null,
    pollingInterval: null,
    selectedFighter: null,
    matchDetails: {},
    matchReplay: null
  },
  
  mutations: {
    SET_TOURNAMENTS(state, tournaments) {
      state.tournaments = tournaments
    },
    SET_ACTIVE_TOURNAMENT(state, tournament) {
      state.activeTournament = tournament
    },
    SET_TOURNAMENT_BRACKET(state, bracket) {
      state.tournamentBracket = bracket
    },
    SET_UPCOMING_MATCHES(state, matches) {
      state.upcomingMatches = matches
    },
    SET_RECENT_MATCHES(state, matches) {
      state.recentMatches = matches
    },
    SET_LOADING(state, { type, value }) {
      state.loading[type] = value
    },
    SET_ERROR(state, error) {
      state.error = error
    },
    SET_POLLING_INTERVAL(state, interval) {
      if (state.pollingInterval) {
        clearInterval(state.pollingInterval)
      }
      state.pollingInterval = interval
    },
    SELECT_FIGHTER(state, fighter) {
      state.selectedFighter = fighter
    },
    SET_MATCH_DETAILS(state, { matchId, details }) {
      state.matchDetails = { ...state.matchDetails, [matchId]: details }
    },
    SET_MATCH_REPLAY(state, replay) {
      state.matchReplay = replay
    },
    CLEAR_MATCH_REPLAY(state) {
      state.matchReplay = null
    }
  },
  
  getters: {
    weeklyTournaments(state) {
      return state.tournaments.filter(t => t.type === 'weekly')
    },
    
    monthlyTournaments(state) {
      return state.tournaments.filter(t => t.type === 'monthly')
    },
    
    activeTournamentStatus(state) {
      if (!state.activeTournament) return 'unknown'
      
      const now = new Date()
      const regEndDate = new Date(state.activeTournament.registrationEndDate)
      const startDate = new Date(state.activeTournament.startDate)
      const endDate = new Date(state.activeTournament.endDate)
      
      if (now < regEndDate) return 'registration'
      if (now < startDate) return 'pending'
      if (now < endDate) return 'active'
      return 'completed'
    },
    
    tournamentStatusText: () => (tournament) => {
      if (!tournament) return 'Desconhecido'
      
      const now = new Date()
      const regEndDate = new Date(tournament.registrationEndDate)
      const startDate = new Date(tournament.startDate)
      const endDate = new Date(tournament.endDate)
      
      if (now < regEndDate) return 'Inscrições Abertas'
      if (now < startDate) return 'Em Breve'
      if (now < endDate) return 'Em Andamento'
      return 'Concluído'
    },
    
    canRegister(state, getters) {
      if (!state.activeTournament) return false
      
      // Check if tournament is in registration period
      if (getters.activeTournamentStatus !== 'registration') return false
      
      // Check if player is already registered
      const userIsRegistered = state.activeTournament.registeredPlayers?.some(
        player => player.id === state.player?.id
      )
      
      // Check if tournament has space
      const hasSpace = state.activeTournament.participants?.length < 
                      state.activeTournament.maxPlayers
      
      return hasSpace && !userIsRegistered
    },
    
    registrationCost(state) {
      return state.activeTournament?.entryFee || 0
    },
    
    featuredMatches(state) {
      const upcoming = [...state.upcomingMatches].slice(0, 5)
      const recent = [...state.recentMatches].slice(0, 3)
      return [...recent, ...upcoming]
    },
    
    getStageLabel: () => (stage) => {
      const labels = {
        'registration': 'Inscrições',
        'qualifiers': 'Qualificatórias',
        'round of 16': 'Oitavas de Final',
        'quarter finals': 'Quartas de Final',
        'semi final': 'Semifinal',
        'final': 'Final',
        'completed': 'Concluído'
      }
      return labels[stage] || stage
    },
    
    getTournamentTypeLabel: () => (type, rules) => {
      const isDeathMatch = rules === 'untilDeath'
      const typeLabel = type === 'weekly' ? 'Semanal' : 'Mensal'
      return `${typeLabel} ${isDeathMatch ? '(Morte Súbita)' : ''}`
    },
    
    getMatchStatusClass: () => (match) => {
      if (match.status === 'live') return 'match-live'
      if (match.status === 'upcoming') return 'match-upcoming'
      return 'match-completed'
    },
    
    getMatchStatusText: () => (match) => {
      if (match.status === 'live') return 'ACONTECENDO AGORA'
      if (match.status === 'upcoming') return 'EM BREVE'
      return 'FINALIZADO'
    }
  },
  
  actions: {
    /**
     * Fetch all active tournaments
     */
    async fetchTournaments({ commit }) {
      commit('SET_LOADING', { type: 'tournaments', value: true })
      commit('SET_ERROR', null)
      
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API_URL}/api/tournaments/active`,
          {
            headers: { Authorization: `Bearer ${Cookies.get('jwt')}` }
          }
        )
        
        if (response.data?.data) {
          commit('SET_TOURNAMENTS', response.data.data)
          return response.data.data
        } else {
          commit('SET_TOURNAMENTS', [])
          return []
        }
      } catch (error) {
        console.error('Error fetching tournaments:', error)
        commit('SET_ERROR', 'Falha ao carregar torneios. Tente novamente mais tarde.')
        return []
      } finally {
        commit('SET_LOADING', { type: 'tournaments', value: false })
      }
    },
    
    /**
     * Fetch specific tournament details
     */
    async fetchTournamentDetails({ commit }, tournamentId) {
      commit('SET_LOADING', { type: 'details', value: true })
      commit('SET_ERROR', null)
      
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API_URL}/api/tournaments/${tournamentId}`,
          {
            headers: { Authorization: `Bearer ${Cookies.get('jwt')}` }
          }
        )
        
        if (response.data?.data) {
          commit('SET_ACTIVE_TOURNAMENT', response.data.data)
          return response.data.data
        }
        return null
      } catch (error) {
        console.error('Error fetching tournament details:', error)
        commit('SET_ERROR', 'Falha ao carregar detalhes do torneio.')
        return null
      } finally {
        commit('SET_LOADING', { type: 'details', value: false })
      }
    },
    
    /**
     * Register a fighter for a tournament
     */
    async registerForTournament({ commit, dispatch }, { tournamentId, fighterId }) {
      if (!fighterId) {
        commit('SET_ERROR', 'Você precisa selecionar um lutador!')
        return { success: false }
      }
      
      commit('SET_LOADING', { type: 'registration', value: true })
      commit('SET_ERROR', null)
      
      try {
        const response = await axios.post(
          `${process.env.VUE_APP_API_URL}/api/tournaments/${tournamentId}/register`,
          { fighterId },
          {
            headers: { Authorization: `Bearer ${Cookies.get('jwt')}` }
          }
        )
        
        if (response.data?.data) {
          // Reload tournament details to update participation status
          await dispatch('fetchTournamentDetails', tournamentId)
          
          // Reset selected fighter
          commit('SELECT_FIGHTER', null)
          
          return { success: true, ...response.data.data }
        }
        
        return { success: false }
      } catch (error) {
        console.error('Error registering for tournament:', error)
        const errorMsg = error.response?.data?.error || 'Falha ao registrar no torneio.'
        commit('SET_ERROR', errorMsg)
        return { success: false, error: errorMsg }
      } finally {
        commit('SET_LOADING', { type: 'registration', value: false })
      }
    },
    
    /**
     * Select a fighter for tournament registration
     */
    selectFighter({ commit }, fighter) {
      commit('SELECT_FIGHTER', fighter)
    },
    
    /**
     * Fetch tournament bracket
     */
    async fetchTournamentBracket({ commit }, tournamentId) {
      commit('SET_LOADING', { type: 'bracket', value: true })
      
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API_URL}/api/tournaments/${tournamentId}/bracket`,
          {
            headers: { Authorization: `Bearer ${Cookies.get('jwt')}` }
          }
        )
        
        if (response.data?.data) {
          commit('SET_TOURNAMENT_BRACKET', response.data.data)
          return response.data.data
        }
        return null
      } catch (error) {
        console.error('Error fetching tournament bracket:', error)
        commit('SET_ERROR', 'Falha ao carregar chaveamento do torneio.')
        return null
      } finally {
        commit('SET_LOADING', { type: 'bracket', value: false })
      }
    },
    
    /**
     * Fetch tournament schedule (upcoming and recent matches)
     */
    async fetchTournamentSchedule({ commit }, tournamentId) {
      commit('SET_LOADING', { type: 'schedule', value: true })
      
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API_URL}/api/tournaments/${tournamentId}/schedule`,
          {
            headers: { Authorization: `Bearer ${Cookies.get('jwt')}` }
          }
        )
        
        if (response.data?.data) {
          const { upcoming, recent } = response.data.data
          commit('SET_UPCOMING_MATCHES', upcoming || [])
          commit('SET_RECENT_MATCHES', recent || [])
          return response.data.data
        }
        return { upcoming: [], recent: [] }
      } catch (error) {
        console.error('Error fetching tournament schedule:', error)
        commit('SET_ERROR', 'Falha ao carregar agenda de lutas.')
        return { upcoming: [], recent: [] }
      } finally {
        commit('SET_LOADING', { type: 'schedule', value: false })
      }
    },
    
    /**
     * Fetch match details
     */
    async fetchMatchDetails({ commit }, matchId) {
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API_URL}/api/matches/${matchId}`,
          {
            headers: { Authorization: `Bearer ${Cookies.get('jwt')}` }
          }
        )
        
        if (response.data?.data) {
          commit('SET_MATCH_DETAILS', { 
            matchId, 
            details: response.data.data 
          })
          return response.data.data
        }
        return null
      } catch (error) {
        console.error('Error fetching match details:', error)
        commit('SET_ERROR', 'Falha ao carregar detalhes da luta.')
        return null
      }
    },
    
    /**
     * Fetch match replay
     */
    async fetchMatchReplay({ commit }, matchId) {
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API_URL}/api/tournaments/match/${matchId}/replay`,
          {
            headers: { Authorization: `Bearer ${Cookies.get('jwt')}` }
          }
        );
        
        if (response.data?.data) {
          const replayData = response.data.data;
          
          // Store replay data
          commit('SET_MATCH_REPLAY', replayData);
          
          // Prepare the battle object with initial values if not present
          const battleData = replayData.battle || {};
          
          // Ensure we have fighter data
          if (!battleData.match && replayData.match) {
            battleData.match = replayData.match;
          }
          
          // Ensure events array exists
          if (!battleData.events && Array.isArray(replayData.events)) {
            battleData.events = replayData.events;
          } else if (!battleData.events) {
            battleData.events = [];
          }
          
          // Add initial values for health/stamina if not present
          if (battleData.match?.challenging_1s?.[0]) {
            battleData.initialHealth1 = battleData.match.challenging_1s[0].health || 100;
            battleData.initialStamina1 = battleData.match.challenging_1s[0].stamina || 100;
          }
          
          if (battleData.match?.challenging_2s?.[0]) {
            battleData.initialHealth2 = battleData.match.challenging_2s[0].health || 100;
            battleData.initialStamina2 = battleData.match.challenging_2s[0].stamina || 100;
          }
          
          // Ensure actions array exists
          if (!battleData.actions) {
            battleData.actions = [];
          }
          
          // Format data for Arena component
          return {
            battle: battleData,
            currentMatch: replayData.match || {
              challenging_1s: replayData.challenging_1s || battleData.match?.challenging_1s || [],
              challenging_2s: replayData.challenging_2s || battleData.match?.challenging_2s || []
            },
            nextMatch: null // Replays don't have next matches
          };
        }
        return null;
      } catch (error) {
        console.error('Error fetching match replay:', error);
        commit('SET_ERROR', 'Falha ao carregar replay da luta.');
        return null;
      }
    },
    
    /**
     * Clear match replay data
     */
    clearMatchReplay({ commit }) {
      commit('CLEAR_MATCH_REPLAY')
    },
    
    /**
     * Start polling for tournament updates
     */
    startPolling({ dispatch, commit }, { tournamentId, interval = 5 * 60 * 1000 }) {
      const polling = setInterval(async () => {
        if (tournamentId) {
          // If specific tournament is provided, update that tournament
          await dispatch('fetchTournamentDetails', tournamentId)
          await dispatch('fetchTournamentBracket', tournamentId)
          await dispatch('fetchTournamentSchedule', tournamentId)
        } else {
          // Otherwise update all tournaments
          await dispatch('fetchTournaments')
        }
      }, interval)
      
      commit('SET_POLLING_INTERVAL', polling)
    },
    
    /**
     * Stop polling
     */
    stopPolling({ commit }) {
      commit('SET_POLLING_INTERVAL', null)
    },
    
    /**
     * Initialize the tournament module
     */
    initialize({ dispatch }) {
      // Load all tournaments
      dispatch('fetchTournaments')
      
      // Start global polling
      dispatch('startPolling', { interval: 5 * 60 * 1000 })
    },
    
    /**
     * Clean up the tournament module
     */
    cleanup({ dispatch }) {
      dispatch('stopPolling')
    },
    
    /**
     * Fetch upcoming matches (used in MatchesListPage)
     */
    async fetchUpcomingMatches({ commit }) {
      commit('SET_LOADING', { type: 'schedule', value: true })
      
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API_URL}/api/tournaments/matches/upcoming`,
          {
            headers: { Authorization: `Bearer ${Cookies.get('jwt')}` }
          }
        )
        
        if (response.data?.data) {
          commit('SET_UPCOMING_MATCHES', response.data.data)
          return response.data.data
        }
        commit('SET_UPCOMING_MATCHES', [])
        return []
      } catch (error) {
        console.error('Error fetching upcoming matches:', error)
        commit('SET_ERROR', 'Falha ao carregar lutas agendadas.')
        commit('SET_UPCOMING_MATCHES', [])
        return []
      } finally {
        commit('SET_LOADING', { type: 'schedule', value: false })
      }
    },
    
    /**
     * Fetch recent matches with pagination
     */
    async fetchRecentMatches({ commit, state }, { page = 1, limit = 10, append = false }) {
      commit('SET_LOADING', { type: 'schedule', value: true })
      
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API_URL}/api/tournaments/matches/recent`,
          {
            params: { page, limit },
            headers: { Authorization: `Bearer ${Cookies.get('jwt')}` }
          }
        )
        
        if (response.data?.data) {
          if (append) {
            commit('SET_RECENT_MATCHES', [...state.recentMatches, ...response.data.data])
          } else {
            commit('SET_RECENT_MATCHES', response.data.data)
          }
          return response.data.data
        }
        
        if (!append) {
          commit('SET_RECENT_MATCHES', [])
        }
        return []
      } catch (error) {
        console.error('Error fetching recent matches:', error)
        commit('SET_ERROR', 'Falha ao carregar lutas recentes.')
        if (!append) {
          commit('SET_RECENT_MATCHES', [])
        }
        return []
      } finally {
        commit('SET_LOADING', { type: 'schedule', value: false })
      }
    },
  }
}
